import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from 'sonner';
import { GameProvider } from './contexts/GameContext';
import Header from './components/Header';
import Footer from './components/Footer';
import MobileNav from './components/MobileNav';
import { CelebrationModal, BadgeToast } from './components/Celebrations';

// Pages - lazy loaded for performance
const HomePage = lazy(() => import('./pages/HomePage'));
const StoriesPage = lazy(() => import('./pages/StoriesPage'));
const ColoringPage = lazy(() => import('./pages/ColoringPage'));
const MathPage = lazy(() => import('./pages/MathPage'));
const ExplorePage = lazy(() => import('./pages/ExplorePage'));
const SongsPage = lazy(() => import('./pages/SongsPage'));
const QuizzesPage = lazy(() => import('./pages/QuizzesPage'));
const GamesHub = lazy(() => import('./pages/GamesHub'));
const BadgesPage = lazy(() => import('./pages/BadgesPage'));
const ParentsPage = lazy(() => import('./pages/ParentsPage'));
const EbooksPage = lazy(() => import('./pages/EbooksPage'));
const BookReader = lazy(() => import('./pages/BookReader'));
const PremiumPage = lazy(() => import('./pages/PremiumPage'));
const CharactersPage = lazy(() => import('./pages/CharactersPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));

// Games
const WordSafari = lazy(() => import('./pages/games/WordSafari'));
const LeoSpells = lazy(() => import('./pages/games/LeoSpells'));
const NumberDash = lazy(() => import('./pages/games/NumberDash'));
const CountingJungle = lazy(() => import('./pages/games/CountingJungle'));
const MemoryMatch = lazy(() => import('./pages/games/MemoryMatch'));
const BrainBlocks = lazy(() => import('./pages/games/BrainBlocks'));
const LeoAdventure = lazy(() => import('./pages/games/LeoAdventure'));
const JungleEscape = lazy(() => import('./pages/games/JungleEscape'));
const AnimalQuiz = lazy(() => import('./pages/games/AnimalQuiz'));
const AnimalSort = lazy(() => import('./pages/games/AnimalSort'));
const DoodleDash = lazy(() => import('./pages/games/DoodleDash'));
const ColorBlast = lazy(() => import('./pages/games/ColorBlast'));
const TemboCounting = lazy(() => import('./pages/games/TemboCounting'));
const BalloonPop = lazy(() => import('./pages/games/BalloonPop'));
const PatternParty = lazy(() => import('./pages/games/PatternParty'));
const LetterMatch = lazy(() => import('./pages/games/LetterMatch'));
const ChessLearn = lazy(() => import('./pages/games/ChessLearn'));
const ChessPlay = lazy(() => import('./pages/games/ChessPlay'));
const SudokuGame = lazy(() => import('./pages/games/SudokuGame'));
const CrosswordGame = lazy(() => import('./pages/games/CrosswordGame'));

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-sunshine border-t-accent-coral rounded-full animate-spin mx-auto" />
        <p className="font-heading text-brown mt-4 text-lg">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <GameProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 font-body text-brown pb-16 lg:pb-0">
          <Toaster position="top-center" richColors />
          <CelebrationModal />
          <BadgeToast />
          <Header />
          <AnimatePresence mode="wait">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/stories" element={<StoriesPage />} />
                <Route path="/coloring" element={<ColoringPage />} />
                <Route path="/math" element={<MathPage />} />
                <Route path="/explore" element={<ExplorePage />} />
                <Route path="/songs" element={<SongsPage />} />
                <Route path="/quizzes" element={<QuizzesPage />} />
                <Route path="/games" element={<GamesHub />} />
                <Route path="/games/word-safari" element={<WordSafari />} />
                <Route path="/games/leo-spells" element={<LeoSpells />} />
                <Route path="/games/number-dash" element={<NumberDash />} />
                <Route path="/games/counting-jungle" element={<CountingJungle />} />
                <Route path="/games/memory-match" element={<MemoryMatch />} />
                <Route path="/games/brain-blocks" element={<BrainBlocks />} />
                <Route path="/games/leo-adventure" element={<LeoAdventure />} />
                <Route path="/games/jungle-escape" element={<JungleEscape />} />
                <Route path="/games/animal-quiz" element={<AnimalQuiz />} />
                <Route path="/games/animal-sort" element={<AnimalSort />} />
                <Route path="/games/doodle-dash" element={<DoodleDash />} />
                <Route path="/games/color-blast" element={<ColorBlast />} />
                <Route path="/badges" element={<BadgesPage />} />
                <Route path="/parents" element={<ParentsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/ebooks" element={<EbooksPage />} />
                <Route path="/ebooks/:bookId" element={<BookReader />} />
                <Route path="/premium" element={<PremiumPage />} />
                <Route path="/characters" element={<CharactersPage />} />
                <Route path="/games/tembo-counting" element={<TemboCounting />} />
                <Route path="/games/balloon-pop" element={<BalloonPop />} />
                <Route path="/games/pattern-party" element={<PatternParty />} />
                <Route path="/games/letter-match" element={<LetterMatch />} />
                <Route path="/games/chess-learn" element={<ChessLearn />} />
                <Route path="/games/chess-play" element={<ChessPlay />} />
                <Route path="/games/sudoku" element={<SudokuGame />} />
                <Route path="/games/crossword" element={<CrosswordGame />} />
              </Routes>
            </Suspense>
          </AnimatePresence>
          <Footer />
          <MobileNav />
        </div>
      </BrowserRouter>
    </GameProvider>
  );
}

export default App;

